import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { updateMyTheme } from "./api";

const ThemeContext = createContext(null);
const STORAGE_KEY = "theme";
const THEME_COLOR = { dark: "#0f1117", light: "#f4f6fa" };

function normalize(theme) {
  return theme === "light" ? "light" : "dark";
}

export function getCachedTheme() {
  try {
    return normalize(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "dark";
  }
}

function applyTheme(theme) {
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[theme]);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable (private mode etc) — theme still applies for this load
  }
}

// serverTheme: the signed-in user's saved preference (from GET /api/auth/me),
// or undefined while logged out / still loading. It's the source of truth
// once known — localStorage is only a pre-login / pre-fetch cache so the
// right theme paints immediately instead of flashing dark then switching.
export function ThemeProvider({ serverTheme, authed, children }) {
  const [theme, setThemeState] = useState(getCachedTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (authed && serverTheme) {
      const normalized = normalize(serverTheme);
      setThemeState((current) => (current === normalized ? current : normalized));
    }
  }, [authed, serverTheme]);

  const setTheme = useCallback(
    (next) => {
      const normalized = normalize(next);
      setThemeState(normalized);
      if (authed) {
        updateMyTheme(normalized).catch(() => {});
      }
    },
    [authed]
  );

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
